import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  getEmployeeOperationMembers,
  getMyEmployeeOperations,
  getWaitingConversations,
  sendEmployeeOperationsHeartbeat,
  updateMyEmployeeOperations,
} from "../api/employeeOperations";
import {
  assignConversation,
  takeConversation,
  unassignConversation,
} from "../api/conversations";
import { getApiError } from "../api/errors";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import MainLayout from "../layouts/MainLayout";
import { formatDate, titleCase } from "../utils/format";

function waitingLabel(seconds) {
  const total = Math.max(0, Number(seconds || 0));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function availabilityColor(status) {
  if (status === "available") return "success";
  if (status === "busy") return "warning";
  return "default";
}

export default function EmployeeOperations() {
  const navigate = useNavigate();
  const {
    canEdit,
    canManage,
    selectedWorkspace,
    selectedStore,
    user,
  } = useAuth();
  const [profile, setProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [waiting, setWaiting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [profileResponse, memberResponse, waitingResponse] = await Promise.all([
        getMyEmployeeOperations(),
        getEmployeeOperationMembers(),
        getWaitingConversations({ limit: 200 }),
      ]);
      setProfile(profileResponse.data);
      setMembers(memberResponse.data?.items || []);
      setWaiting(waitingResponse.data?.items || []);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load employee operations."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load, selectedWorkspace?.public_id, selectedStore?.public_id]);

  useEffect(() => {
    const poll = window.setInterval(load, 15000);
    const heartbeat = window.setInterval(() => {
      sendEmployeeOperationsHeartbeat().catch(() => {});
    }, 60000);
    sendEmployeeOperationsHeartbeat().catch(() => {});
    return () => {
      window.clearInterval(poll);
      window.clearInterval(heartbeat);
    };
  }, [load]);

  const availableMembers = useMemo(
    () => members.filter((item) => item.availability_status !== "offline"),
    [members],
  );

  const updateProfile = async (changes) => {
    setSaving(true);
    setError("");
    try {
      const response = await updateMyEmployeeOperations(changes);
      setProfile(response.data);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not update notification settings."));
    } finally {
      setSaving(false);
    }
  };

  const take = async (publicId) => {
    setActionId(publicId);
    setError("");
    try {
      await takeConversation(publicId);
      await load();
      navigate(`/conversations?open=${publicId}`);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not take the conversation."));
      await load();
    } finally {
      setActionId("");
    }
  };

  const assign = async (publicId, userPublicId) => {
    if (!userPublicId) return;
    setActionId(publicId);
    setError("");
    try {
      await assignConversation(publicId, userPublicId);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not assign the conversation."));
    } finally {
      setActionId("");
    }
  };

  const unassign = async (publicId) => {
    setActionId(publicId);
    setError("");
    try {
      await unassignConversation(publicId);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not return the conversation to the waiting queue."));
    } finally {
      setActionId("");
    }
  };

  return (
    <MainLayout>
      <PageHeader
        title="Employee Queue"
        description="Customers waiting for a human answer, longest wait first."
        actions={(
          <Button variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: "none", border: "1px solid #e2e8f0" }}>
        <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
          <Stack direction={{ xs: "column", lg: "row" }} gap={2.5} justifyContent="space-between">
            <Box sx={{ minWidth: { lg: 260 } }}>
              <Typography variant="h6" fontWeight={900}>My availability</Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
                Available receives new handoff pushes. Busy keeps in-app alerts. Offline pauses phone pushes.
              </Typography>
              <FormControl fullWidth sx={{ mt: 2, maxWidth: 280 }}>
                <InputLabel>Availability</InputLabel>
                <Select
                  label="Availability"
                  value={profile?.availability_status || "available"}
                  onChange={(event) => updateProfile({ availability_status: event.target.value })}
                  disabled={saving || !profile}
                >
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="busy">Busy</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack spacing={0.4} sx={{ minWidth: { lg: 360 } }}>
              <Typography variant="h6" fontWeight={900}>Phone notification preferences</Typography>
              {[
                ["handoff_push_enabled", "New customer handoffs"],
                ["assigned_push_enabled", "Conversations assigned to me"],
                ["overdue_push_enabled", "Overdue handoff escalations"],
                ["order_push_enabled", "Confirmed customer orders"],
                ["sound_enabled", "Notification sound preference"],
              ].map(([field, label]) => (
                <FormControlLabel
                  key={field}
                  control={(
                    <Switch
                      checked={Boolean(profile?.[field])}
                      onChange={(event) => updateProfile({ [field]: event.target.checked })}
                      disabled={saving || !profile}
                    />
                  )}
                  label={label}
                />
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip label={`${waiting.length} waiting`} color={waiting.length ? "warning" : "success"} />
        <Chip label={`${members.filter((item) => item.availability_status === "available").length} available employees`} variant="outlined" />
        <Chip label={`${members.filter((item) => item.availability_status === "busy").length} busy`} variant="outlined" />
      </Stack>

      {loading ? (
        <LoadingPanel label="Loading employee queue…" />
      ) : waiting.length === 0 ? (
        <EmptyPanel
          icon="✅"
          title="Nobody is waiting"
          description="New customer requests for an employee will appear here automatically."
        />
      ) : (
        <Stack spacing={1.5}>
          {waiting.map(({ conversation, waiting_seconds, overdue }) => (
            <Card
              key={conversation.public_id}
              sx={{
                borderRadius: 3,
                boxShadow: "none",
                border: "1px solid",
                borderColor: overdue ? "error.main" : "warning.main",
              }}
            >
              <CardContent sx={{ p: { xs: 2.2, md: 2.6 } }}>
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="h6" fontWeight={900}>{conversation.customer.display_name}</Typography>
                      <Chip size="small" label={`Waiting ${waitingLabel(waiting_seconds)}`} color={overdue ? "error" : "warning"} />
                      {conversation.handoff_escalation_level > 0 && (
                        <Chip
                          size="small"
                          label={conversation.handoff_escalation_level >= 2 ? "Overdue" : "Escalated"}
                          color={conversation.handoff_escalation_level >= 2 ? "error" : "warning"}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    <Typography sx={{ mt: 0.8 }} color="text.secondary">
                      {conversation.last_message_preview || "Customer requested an employee."}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.8 }}>
                      Requested {formatDate(conversation.handoff_requested_at)} · {titleCase(conversation.provider)}
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => take(conversation.public_id)}
                      disabled={!canEdit || actionId === conversation.public_id}
                    >
                      Take
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/conversations?open=${conversation.public_id}`)}
                    >
                      Open
                    </Button>
                    {canManage && (
                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel>Assign employee</InputLabel>
                        <Select
                          label="Assign employee"
                          value=""
                          onChange={(event) => assign(conversation.public_id, event.target.value)}
                          disabled={actionId === conversation.public_id}
                        >
                          {availableMembers.map((member) => (
                            <MenuItem key={member.user_public_id} value={member.user_public_id}>
                              {member.username} · {titleCase(member.availability_status)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {canManage && conversation.assigned_user_public_id && (
                      <Button
                        color="inherit"
                        onClick={() => unassign(conversation.public_id)}
                        disabled={actionId === conversation.public_id}
                      >
                        Unassign
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Card sx={{ mt: 3, borderRadius: 3, boxShadow: "none", border: "1px solid #e2e8f0" }}>
        <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
          <Typography variant="h6" fontWeight={900}>Employee status</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            {members.map((member) => (
              <Chip
                key={member.user_public_id}
                label={`${member.username}: ${titleCase(member.availability_status)}`}
                color={availabilityColor(member.availability_status)}
                variant={member.user_public_id === user?.public_id ? "filled" : "outlined"}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </MainLayout>
  );
}
